import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import {
  Camera, Upload, Loader2, CheckCircle2, Flame, Dumbbell, Droplet,
  AlertCircle, Trash2, Sparkles, X,
} from 'lucide-react';
import { API } from '../context/AuthContext';
import { useLang } from '../context/LanguageContext';

const MAX_BYTES = 5 * 1024 * 1024; // 5 MB

// Downscale + JPEG-compress an image on the client so payloads stay small.
const compressImage = (file, maxDim = 1024, quality = 0.82) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onerror = () => reject(new Error('read_failed'));
  reader.onload = () => {
    const img = new Image();
    img.onerror = () => reject(new Error('image_decode_failed'));
    img.onload = () => {
      let { width, height } = img;
      if (width > maxDim || height > maxDim) {
        if (width > height) {
          height = Math.round((maxDim / width) * height);
          width = maxDim;
        } else {
          width = Math.round((maxDim / height) * width);
          height = maxDim;
        }
      }
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, width, height);
      resolve(canvas.toDataURL('image/jpeg', quality));
    };
    img.src = reader.result;
  };
  reader.readAsDataURL(file);
});

const MealTracker = () => {
  const { t } = useLang();
  const fileRef = useRef(null);
  const cameraRef = useRef(null);
  const [imageDataUrl, setImageDataUrl] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [result, setResult] = useState(null); // AI response
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [today, setToday] = useState({ items: [], totals: { calories: 0, protein: 0, fat: 0, carbs: 0 } });
  const [toast, setToast] = useState('');

  const loadToday = async () => {
    try {
      const res = await axios.get(`${API}/meals/today`, { withCredentials: true });
      setToday(res.data);
    } catch (_) {
      // ignore silently
    }
  };

  useEffect(() => {
    loadToday();
  }, []);

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(''), 3000);
  };

  const handleFile = async (file) => {
    setError('');
    setResult(null);
    if (!file) return;
    if (file.size > MAX_BYTES) {
      setError(t('imageTooLarge'));
      return;
    }
    try {
      const dataUrl = await compressImage(file);
      setImageDataUrl(dataUrl);
      await analyze(dataUrl);
    } catch (e) {
      setError(t('analyzeFailed'));
    }
  };

  const analyze = async (dataUrl) => {
    setError('');
    setAnalyzing(true);
    try {
      const base64 = dataUrl.startsWith('data:') ? dataUrl.split(',')[1] : dataUrl;
      const res = await axios.post(
        `${API}/meals/analyze`,
        { image_base64: base64, mime_type: 'image/jpeg' },
        { withCredentials: true, timeout: 60000 }
      );
      setResult(res.data);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 422) setError(t('noFoodDetected'));
      else if (err?.response?.status === 402) setError('Active subscription required.');
      else setError(detail || t('analyzeFailed'));
    } finally {
      setAnalyzing(false);
    }
  };

  const saveMeal = async () => {
    if (!result) return;
    setSaving(true);
    try {
      await axios.post(
        `${API}/meals/log`,
        {
          name: result.name || 'Meal',
          calories: result.calories || 0,
          protein: result.protein || 0,
          fat: result.fat || 0,
          carbs: result.carbs || 0,
          portion_grams: result.portion_grams || null,
          ingredients: result.ingredients || [],
        },
        { withCredentials: true }
      );
      showToast(t('mealSaved'));
      reset();
      loadToday();
    } catch (e) {
      setError(e?.response?.data?.detail || t('analyzeFailed'));
    } finally {
      setSaving(false);
    }
  };

  const deleteMeal = async (id) => {
    try {
      await axios.delete(`${API}/meals/${id}`, { withCredentials: true });
      loadToday();
    } catch (_) {}
  };

  const reset = () => {
    setImageDataUrl(null);
    setResult(null);
    setError('');
    if (fileRef.current) fileRef.current.value = '';
    if (cameraRef.current) cameraRef.current.value = '';
  };

  return (
    <div className="bg-[#1a1b1e] border border-[#2a2b2f] rounded-md p-6" data-testid="meal-tracker">
      <div className="flex items-center justify-between mb-1 gap-3 flex-wrap">
        <div>
          <h2 className="text-lg font-bold flex items-center gap-2">
            <Sparkles size={16} className="text-[#ff4e15]" />
            {t('trackMealTitle')}
          </h2>
          <p className="text-xs text-white/60 mt-0.5">{t('trackMealSub')}</p>
        </div>
      </div>

      {/* Hidden file inputs */}
      <input
        ref={fileRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        onChange={(e) => handleFile(e.target.files?.[0])}
        className="hidden"
        data-testid="meal-file-input"
      />
      <input
        ref={cameraRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={(e) => handleFile(e.target.files?.[0])}
        className="hidden"
        data-testid="meal-camera-input"
      />

      {/* Uploader / preview */}
      {!imageDataUrl && !analyzing && (
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <button
            onClick={() => cameraRef.current?.click()}
            className="py-8 rounded-sm border-2 border-dashed border-[#2a2b2f] hover:border-[#ff4e15] hover:bg-[#ff4e15]/5 transition-colors flex flex-col items-center gap-2 text-white/80"
            data-testid="meal-take-photo-btn"
          >
            <Camera size={26} className="text-[#ff4e15]" />
            <span className="text-sm font-semibold">{t('takePhoto')}</span>
          </button>
          <button
            onClick={() => fileRef.current?.click()}
            className="py-8 rounded-sm border-2 border-dashed border-[#2a2b2f] hover:border-[#ff4e15] hover:bg-[#ff4e15]/5 transition-colors flex flex-col items-center gap-2 text-white/80"
            data-testid="meal-upload-btn"
          >
            <Upload size={26} className="text-[#ff4e15]" />
            <span className="text-sm font-semibold">{t('uploadPhoto')}</span>
          </button>
        </div>
      )}

      {imageDataUrl && (
        <div className="mt-4">
          <div className="relative rounded-sm overflow-hidden border border-[#2a2b2f] max-w-md mx-auto">
            <img src={imageDataUrl} alt="meal" className="w-full max-h-72 object-cover" />
            {!analyzing && (
              <button
                onClick={reset}
                className="absolute top-2 right-2 bg-black/60 backdrop-blur-sm rounded-full p-1.5 text-white hover:bg-black/80"
                aria-label="clear"
                data-testid="meal-clear-btn"
              >
                <X size={14} />
              </button>
            )}
          </div>

          {analyzing && (
            <div className="mt-4 flex items-center justify-center gap-2 text-sm text-white/70" data-testid="meal-analyzing">
              <Loader2 size={16} className="animate-spin text-[#ff4e15]" />
              {t('analyzing')}
            </div>
          )}
        </div>
      )}

      {error && (
        <div className="mt-4 flex items-start gap-2 text-sm text-[#ff4e15] bg-[#1f1210] border border-[#ff4e15]/40 px-3 py-2 rounded-sm" data-testid="meal-error">
          <AlertCircle size={14} className="mt-0.5 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* Analysis result */}
      {result && !analyzing && (
        <div className="mt-5 p-4 bg-[#141518] border border-[#ff4e15]/30 rounded-sm fade-in-up" data-testid="meal-result">
          <div className="flex items-center gap-2 text-[10px] text-[#ff4e15] uppercase tracking-wide font-bold mb-1">
            <CheckCircle2 size={12} /> {t('detected')}
          </div>
          <div className="text-lg font-bold">{result.name}</div>

          {result.portion_grams ? (
            <div className="text-xs text-white/60 mt-0.5">{t('portion')}: ~{result.portion_grams} g</div>
          ) : null}

          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-4 text-center">
            <Stat icon={Flame} label={t('calories')} value={`${result.calories}`} sub="kcal" />
            <Stat icon={Dumbbell} label={t('protein')} value={`${result.protein || 0}`} sub="g" />
            <Stat icon={Droplet} label={t('fatShort')} value={`${result.fat || 0}`} sub="g" />
            <Stat icon={Flame} label={t('carbs')} value={`${result.carbs || 0}`} sub="g" />
          </div>

          {Array.isArray(result.ingredients) && result.ingredients.length > 0 && (
            <div className="mt-4">
              <div className="text-[10px] text-white/50 uppercase tracking-wide mb-1">{t('ingredients')}</div>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-1">
                {result.ingredients.map((ing, i) => (
                  <li key={i} className="text-xs text-white/80 flex items-start gap-2">
                    <span className="w-1 h-1 rounded-full bg-[#ff4e15] mt-1.5 shrink-0" />
                    {ing}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {result.confidence && (
            <div className="mt-3 text-[10px] text-white/50">
              {t('confidence')}: <span className="text-white/70 capitalize">{result.confidence}</span>
              {result.notes ? ` · ${result.notes}` : ''}
            </div>
          )}

          <div className="mt-5 flex gap-2 flex-wrap">
            <button
              onClick={saveMeal}
              disabled={saving}
              className="flex-1 min-w-[140px] py-2.5 rounded-sm font-bold uppercase tracking-wider text-white bg-[#ff4e15] hover:bg-[#ff6033] disabled:opacity-60 transition-colors text-sm"
              data-testid="meal-save-btn"
            >
              {saving ? t('processing') : t('saveMeal')}
            </button>
            <button
              onClick={reset}
              className="px-4 py-2.5 rounded-sm border border-[#2a2b2f] hover:border-[#ff4e15] hover:text-[#ff4e15] transition-colors text-sm"
              data-testid="meal-cancel-btn"
            >
              {t('cancel')}
            </button>
          </div>
        </div>
      )}

      {/* Today's log */}
      <div className="mt-6">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-bold">{t('todaysMeals')}</h3>
          <div className="text-xs text-white/60">
            {t('dailyTotals')}:{' '}
            <span className="text-[#ff4e15] font-bold">{today.totals.calories}</span> kcal
            {' · '}P {today.totals.protein}g
            {' · '}F {today.totals.fat}g
            {' · '}C {today.totals.carbs}g
          </div>
        </div>

        {today.items.length === 0 ? (
          <div className="text-xs text-white/50 py-4 text-center border border-dashed border-[#2a2b2f] rounded-sm">
            {t('noMealsToday')}
          </div>
        ) : (
          <div className="space-y-2">
            {today.items.map((m) => (
              <div key={m.id} className="flex items-center gap-3 p-3 bg-[#141518] border border-[#2a2b2f] rounded-sm" data-testid={`meal-item-${m.id}`}>
                <div className="w-10 h-10 rounded-sm bg-[#ff4e15]/15 flex items-center justify-center shrink-0">
                  <Flame size={16} className="text-[#ff4e15]" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-white font-semibold truncate">{m.name}</div>
                  <div className="text-[10px] text-white/50 mt-0.5">
                    {m.calories} kcal · P {m.protein}g · F {m.fat}g · C {m.carbs}g
                  </div>
                </div>
                <button
                  onClick={() => deleteMeal(m.id)}
                  className="p-2 rounded-sm text-white/40 hover:text-red-400 hover:bg-red-400/10 transition-colors"
                  aria-label={t('delete')}
                  data-testid={`meal-delete-${m.id}`}
                >
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {toast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 bg-[#ff4e15] text-white px-4 py-2 rounded-sm text-sm font-semibold shadow-lg flex items-center gap-2 fade-in-up" data-testid="meal-toast">
          <CheckCircle2 size={14} /> {toast}
        </div>
      )}
    </div>
  );
};

const Stat = ({ icon: Icon, label, value, sub }) => (
  <div className="p-3 bg-[#0d0e10] border border-[#2a2b2f] rounded-sm">
    <Icon size={14} className="text-[#ff4e15] mx-auto mb-1" />
    <div className="text-[10px] text-white/50 uppercase tracking-wide">{label}</div>
    <div className="text-sm font-bold mt-0.5">{value}</div>
    <div className="text-[10px] text-white/40 mt-0.5">{sub}</div>
  </div>
);

export default MealTracker;
